/**
 * MomentContext - Centralized context for moment metadata
 *
 * Provides momentName and momentType to child components in the SlideOutPanel.
 */

import React, { createContext, useContext, ReactNode } from 'react';

interface MomentContextValue {
  momentName: string;
  momentType: string;
  issueId: string;
}

const MomentContext = createContext<MomentContextValue | undefined>(undefined);

interface MomentProviderProps {
  children: ReactNode;
  momentName: string;
  momentType: string;
  issueId: string;
}

export const MomentProvider: React.FC<MomentProviderProps> = ({
  children,
  momentName,
  momentType,
  issueId
}) => {
  // 🔍 DEBUG: Log context values
  React.useEffect(() => {
    console.log('🎯 MomentContext initialized:', {
      momentName,
      momentType,
      issueId
    });
  }, [momentName, momentType, issueId]);

  return (
    <MomentContext.Provider value={{ momentName, momentType, issueId }}>
      {children}
    </MomentContext.Provider>
  );
};

/**
 * Hook to access moment metadata from anywhere in the SlideOutPanel
 *
 * @returns {MomentContextValue} momentName, momentType, issueId
 * @throws Error if used outside MomentProvider
 */
export const useMomentContext = (): MomentContextValue => {
  const context = useContext(MomentContext);

  if (!context) {
    throw new Error('useMomentContext must be used within a MomentProvider');
  }

  return context;
};
